// SkipJobDetailPage — /skip/jobs/:id.
//
// Two-column read-only detail view + an action sidebar driven by
// <DynamicActionButtons>. The button row is dynamically derived from
// the per-job action list returned by GET /skip/jobs/:enc_id/available-actions
// (Phase 2b). Each button dispatches to the matching modal:
//   - request_collection / request_exchange / request_tip_return /
//     mark_complete / cancel / postpone → ConfirmActionModal
//   - complete_collection → ProcessCollectionModal
//   - process_exchange    → ProcessExchangeModal
//   - process_tip_return  → ProcessTipReturnModal

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Pencil } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { useQueryClient } from '@tanstack/react-query';

import {
  useChildSkipJobs,
  useSkipJob,
  useUpdateSkipJob,
  useUpdateSkipJobPriority,
} from '../hooks/useSkipJobs';
import { SkipJobStatusBadge } from './components/SkipJobStatusBadge';
import { DynamicActionButtons } from './components/DynamicActionButtons';
import { AttachmentSection } from '../components/AttachmentSection';
import { RevertStatusModal } from './components/RevertStatusModal';
import {
  SitePicker,
  type SitePickerValue,
} from '../../../components/ui/SitePicker/SitePicker';
import { queryKeys } from '../../../api/queryKeys';
import { extractErrorMessage } from '../../../utils/errorMessage';
import styles from './SkipJobDetailPage.module.css';

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * Date + time read-out for stage-transition timestamps
 * (delivered_at / collected_at / completed_at / cancelled_at).
 * The server stamps these via NOW() on every transition; the detail
 * page surfaces them so users can see exactly when each action took
 * place. Time is shown in the user's local timezone.
 */
function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function fmtAddress(...parts: Array<string | null | undefined>): string {
  return parts.filter((p) => typeof p === 'string' && p.length > 0).join(', ') || '—';
}

export function SkipJobDetailPage(): JSX.Element {
  const { id: routeId = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const jobQuery = useSkipJob(routeId);
  // The route may carry a raw uuid (incoming links from
  // SkipInventory* and SkipEnquiryDetailPage). Prefer the job's own
  // enc_id for outbound API calls + cache keys once data is loaded.
  const encId = jobQuery.data?.job.enc_id ?? routeId;

  const priorityMutation = useUpdateSkipJobPriority(encId);
  const updateJobMutation = useUpdateSkipJob(encId);
  const qc = useQueryClient();

  const [siteEditing, setSiteEditing] = useState(false);
  const [siteDraft, setSiteDraft] = useState<SitePickerValue>({ type: 'none' });
  const [siteError, setSiteError] = useState<string | null>(null);

  const [priorityEditing, setPriorityEditing] = useState(false);
  const [priorityNotesDraft, setPriorityNotesDraft] = useState('');
  const [priorityError, setPriorityError] = useState<string | null>(null);

  // Feature 1 — Revert Status modal. Hidden when the job is in a
  // terminal status (the server returns 409 anyway, but the UI hides
  // the trigger to keep the action panel clean).
  const [revertOpen, setRevertOpen] = useState(false);

  // Child jobs (exchange / tip-return spawns). The list endpoint
  // resolves `parent_job_uuid` to a tenant-scoped parent_job_id; when
  // the parent has no children the response is `{ jobs: [], … }` and
  // the section below is not rendered.
  const childJobsQuery = useChildSkipJobs(jobQuery.data?.job.uuid);
  const childJobs = childJobsQuery.data?.jobs ?? [];

  if (jobQuery.isLoading) {
    return (
      <div className={styles.page}>
        <PageHeader title="Loading job…" />
      </div>
    );
  }

  if (jobQuery.isError || !jobQuery.data) {
    return (
      <div className={styles.page}>
        <PageHeader title="Job not found" />
        <Card>
          <p className={styles.empty}>This job could not be loaded — it may have been deleted.</p>
          <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate('/skip/jobs')}>
            Back to Jobs
          </Button>
        </Card>
      </div>
    );
  }

  const job = jobQuery.data.job;
  const isTerminal = job.status === 'completed' || job.status === 'cancelled';

  function handleActionComplete(): void {
    void jobQuery.refetch();
    void qc.invalidateQueries({
      queryKey: queryKeys.skipJobs.availableActions(encId),
    });
  }

  function handlePriorityMark() {
    setPriorityError(null);
    setPriorityNotesDraft(job.priority_notes ?? '');
    setPriorityEditing(true);
  }

  function handleSaveSite() {
    setSiteError(null);
    const body: Record<string, unknown> = {};
    if (siteDraft.type === 'site') {
      body['site_enc_id'] = siteDraft.site_enc_id;
    } else if (siteDraft.type === 'one_off') {
      body['site_enc_id'] = null;
      body['job_address_line1'] = siteDraft.line1;
      body['job_address_line2'] = siteDraft.line2;
      body['job_city'] = siteDraft.city;
      body['job_postcode'] = siteDraft.postcode;
      body['use_different_job_address'] = true;
    } else {
      setSiteError('Choose a saved site or fill in a one-off address.');
      return;
    }
    updateJobMutation.mutate(body, {
      onSuccess: () => {
        setSiteEditing(false);
      },
      onError: (err) => setSiteError(extractErrorMessage(err)),
    });
  }

  function handlePriorityConfirm(turnOn: boolean) {
    setPriorityError(null);
    priorityMutation.mutate(
      {
        is_priority: turnOn,
        priority_notes: turnOn ? (priorityNotesDraft.trim() || null) : null,
      },
      {
        onSuccess: () => {
          setPriorityEditing(false);
          setPriorityNotesDraft('');
        },
        onError: (err) =>
          setPriorityError(extractErrorMessage(err)),
      },
    );
  }

  const showWorkflowSection =
    job.collection_status !== 'not_requested' ||
    job.exchange_status !== 'not_requested' ||
    job.tip_return_status !== 'not_requested';

  return (
    <div className={styles.page}>
      <PageHeader
        title={`Job ${job.job_number}`}
        subtitle="View and manage job details"
        actions={
          <div className={styles.headerActions}>
            <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate('/skip/jobs')}>
              Back to Jobs
            </Button>
            <Button
              variant="primary"
              icon={Pencil}
              disabled={isTerminal}
              onClick={() => navigate(`/skip/jobs/${encodeURIComponent(job.enc_id)}/edit`)}
            >
              Edit
            </Button>
          </div>
        }
      />

      {job.is_priority || priorityEditing ? (
        <Card>
          <div className={styles.priorityHeader}>
            {job.is_priority ? (
              <span className={styles.priorityBadge}>Priority</span>
            ) : null}
            {!priorityEditing ? (
              <Button
                variant={job.is_priority ? 'secondary' : 'ghost'}
                onClick={
                  job.is_priority
                    ? () => handlePriorityConfirm(false)
                    : handlePriorityMark
                }
                loading={priorityMutation.isPending}
                disabled={isTerminal}
              >
                {job.is_priority ? 'Remove Priority' : 'Mark Priority'}
              </Button>
            ) : null}
          </div>
          {job.is_priority && job.priority_notes && !priorityEditing ? (
            <p className={styles.priorityNotes}>{job.priority_notes}</p>
          ) : null}
          {priorityEditing ? (
            <div className={styles.priorityEdit}>
              <label htmlFor="priority-notes" className={styles.priorityLabel}>
                Priority notes (optional)
              </label>
              <textarea
                id="priority-notes"
                rows={2}
                value={priorityNotesDraft}
                onChange={(e) => setPriorityNotesDraft(e.target.value)}
                maxLength={5000}
              />
              <div className={styles.priorityActions}>
                <Button
                  variant="primary"
                  onClick={() => handlePriorityConfirm(true)}
                  loading={priorityMutation.isPending}
                >
                  Confirm Priority
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setPriorityEditing(false);
                    setPriorityError(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
              {priorityError ? (
                <p className={styles.priorityError}>{priorityError}</p>
              ) : null}
            </div>
          ) : null}
        </Card>
      ) : (
        <Card>
          <Button
            variant="ghost"
            onClick={handlePriorityMark}
            disabled={isTerminal}
          >
            Mark Priority
          </Button>
        </Card>
      )}

      <div className={styles.layout}>
        <div className={styles.main}>
          <Card>
            <header className={styles.sectionHeader}>
              <h2>Customer Details</h2>
              <SkipJobStatusBadge status={job.status} />
            </header>
            <dl className={styles.dl}>
              <dt>Customer</dt>
              <dd>
                {job.customer_uuid ? (
                  <Link to={`/skip/customers/${encodeURIComponent(job.customer_uuid)}`}>
                    {job.customer_name}
                  </Link>
                ) : (
                  job.customer_name
                )}
              </dd>
              <dt>Phone</dt>
              <dd>{job.customer_phone}</dd>
              <dt>Email</dt>
              <dd>{job.customer_email ?? '—'}</dd>
              <dt>Postcode</dt>
              <dd>{job.customer_postcode}</dd>
              <dt>Job Address</dt>
              <dd>
                {job.site_enc_id != null
                  ? fmtAddress(
                      job.site_label,
                      job.site_address_line1,
                      job.site_address_line2,
                      job.site_city,
                      job.site_postcode,
                    )
                  : fmtAddress(
                      job.job_address_line1,
                      job.job_address_line2,
                      job.job_city,
                      job.job_postcode,
                    )}
              </dd>
            </dl>
          </Card>

          {/* Phase 6D — Job Site card with inline edit (SitePicker). */}
          <Card>
            <header className={styles.sectionHeader}>
              <h2>Job Site</h2>
              {!siteEditing && !isTerminal ? (
                <Button
                  variant="ghost"
                  onClick={() => {
                    setSiteEditing(true);
                    setSiteError(null);
                    setSiteDraft({ type: 'none' });
                  }}
                >
                  Edit Site
                </Button>
              ) : null}
            </header>
            {siteEditing ? (
              <div className={styles.siteEdit}>
                <SitePicker
                  customerEncId={job.customer_uuid}
                  initialSiteUuid={job.site_uuid}
                  initialJobAddress={{
                    line1: job.job_address_line1,
                    line2: job.job_address_line2,
                    city: job.job_city,
                    postcode: job.job_postcode,
                  }}
                  onChange={(value) => setSiteDraft(value)}
                />
                {siteError ? <p className={styles.siteError}>{siteError}</p> : null}
                <div className={styles.siteActions}>
                  <Button
                    variant="primary"
                    onClick={handleSaveSite}
                    loading={updateJobMutation.isPending}
                    disabled={siteDraft.type === 'none'}
                  >
                    Save Site
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setSiteEditing(false);
                      setSiteError(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <p className={styles.siteRead}>
                {job.site_enc_id != null
                  ? fmtAddress(
                      job.site_label,
                      job.site_address_line1,
                      job.site_address_line2,
                      job.site_city,
                      job.site_postcode,
                    )
                  : fmtAddress(
                      job.job_address_line1,
                      job.job_address_line2,
                      job.job_city,
                      job.job_postcode,
                    )}
              </p>
            )}
          </Card>

          <Card>
            <header className={styles.sectionHeader}>
              <h2>Stock Details</h2>
            </header>
            <dl className={styles.dl}>
              <dt>Stock Type</dt>
              <dd>{job.skip_stock_type_name ?? job.skip_type}</dd>
              <dt>Stock Unit Number</dt>
              <dd>{job.skip_number ?? '—'}</dd>
              <dt>Job Type</dt>
              <dd>{job.job_type.replaceAll('_', ' ')}</dd>
            </dl>
          </Card>

          <Card>
            <header className={styles.sectionHeader}>
              <h2>Delivery &amp; Collection</h2>
            </header>
            <dl className={styles.dl}>
              <dt>Delivery Date</dt>
              <dd>{fmtDate(job.delivery_date)}</dd>
              <dt>Delivery Time</dt>
              <dd>{job.delivery_time ?? '—'}</dd>
              <dt>Driver</dt>
              <dd>{job.driver_name ?? 'Unassigned'}</dd>
              <dt>Collection Date</dt>
              <dd>{fmtDate(job.collection_date)}</dd>
              <dt>Collection Time</dt>
              <dd>{job.collection_time ?? '—'}</dd>
              <dt>Delivered At</dt>
              <dd>{fmtDateTime(job.delivered_at)}</dd>
              <dt>Collected At</dt>
              <dd>{fmtDateTime(job.collected_at)}</dd>
              <dt>Completed At</dt>
              <dd>{fmtDateTime(job.completed_at)}</dd>
            </dl>
          </Card>

          {showWorkflowSection ? (
            <Card>
              <header className={styles.sectionHeader}>
                <h2>Workflow Status</h2>
              </header>
              <dl className={styles.dl}>
                <dt>Collection</dt>
                <dd>{job.collection_status.replaceAll('_', ' ')}</dd>
                <dt>Exchange</dt>
                <dd>{job.exchange_status.replaceAll('_', ' ')}</dd>
                <dt>Tip &amp; Return</dt>
                <dd>{job.tip_return_status.replaceAll('_', ' ')}</dd>
              </dl>
            </Card>
          ) : null}

          <Card>
            <header className={styles.sectionHeader}>
              <h2>Notes</h2>
            </header>
            <p className={styles.notes}>{job.notes ?? <em>No notes.</em>}</p>
          </Card>

          <Card>
            <header className={styles.sectionHeader}>
              <h2>Attachments</h2>
            </header>
            <AttachmentSection
              kind="job"
              encId={job.enc_id}
              readOnly={isTerminal}
            />
          </Card>

          {childJobs.length > 0 ? (
            <Card>
              <header className={styles.sectionHeader}>
                <h2>Child Jobs</h2>
              </header>
              <table className={styles.childTable}>
                <thead>
                  <tr>
                    <th>Job #</th>
                    <th>Type</th>
                    <th>Delivery</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {childJobs.map((c) => (
                    <tr key={c.enc_id}>
                      <td>
                        <Link to={`/skip/jobs/${encodeURIComponent(c.enc_id)}`}>
                          {c.job_number}
                        </Link>
                      </td>
                      <td>{c.job_type.replaceAll('_', ' ')}</td>
                      <td>{fmtDate(c.delivery_date)}</td>
                      <td>
                        <SkipJobStatusBadge status={c.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          ) : null}
        </div>

        <aside className={styles.sidebar}>
          <Card>
            <header className={styles.sectionHeader}>
              <h2>Actions</h2>
            </header>
            <DynamicActionButtons
              jobEncId={job.enc_id}
              jobNumber={job.job_number}
              customerName={job.customer_name}
              skipSizeEncId={job.skip_size_enc_id}
              onActionComplete={handleActionComplete}
            />
            {!isTerminal ? (
              <Button
                variant="ghost"
                onClick={() => setRevertOpen(true)}
                aria-label="Revert job status to its previous value"
              >
                Revert Status
              </Button>
            ) : null}
            {isTerminal ? (
              <p className={styles.terminalHint}>
                This job is {job.status} and has no available actions.
              </p>
            ) : null}
          </Card>
        </aside>
      </div>

      {revertOpen ? (
        <RevertStatusModal
          isOpen
          jobEncId={job.enc_id}
          jobNumber={job.job_number}
          currentStatus={job.status}
          onSuccess={handleActionComplete}
          onClose={() => setRevertOpen(false)}
        />
      ) : null}
    </div>
  );
}
